import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { Currency } from '../../../shared/utils/paymentTypes';

const TEMPLATES_STORAGE_KEY = 'mc_payment_templates';

const loadTemplatesFromStorage = (): PaymentTemplate[] => {
  try {
    const raw = localStorage.getItem(TEMPLATES_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as PaymentTemplate[]) : [];
  } catch {
    return [];
  }
};

/** Data carried when retrying a cancelled/failed payment (no IBAN — must be re-entered). */
export interface RetryPaymentData {
  amount: string;
  currency: Currency;
  recipientName: string;
  reference: string;
}

/** Reusable payment template saved from an executed payment. */
export interface PaymentTemplate {
  id: string; // unique id — payment.id of the source payment
  name: string; // display label, e.g. "€950 · John Smith · Rent Jan"
  amount: string;
  currency: Currency;
  recipientName: string;
  reference: string;
}

/** BANKSapi PAY/Connect session, carried from PayRent to the /payment widget page. */
export interface CheckoutSession {
  sessionId: string;
  transactionId: string;
}

interface PaymentState {
  retryData: RetryPaymentData | null;
  templates: PaymentTemplate[];
  checkoutSession: CheckoutSession | null;
}

const initialState: PaymentState = {
  retryData: null,
  templates: loadTemplatesFromStorage(),
  checkoutSession: null,
};

const paymentSlice = createSlice({
  name: 'payment',
  initialState,
  reducers: {
    setRetryPayment: (state, action: PayloadAction<RetryPaymentData>) => {
      state.retryData = action.payload;
    },
    clearRetryPayment: state => {
      state.retryData = null;
    },
    saveTemplate: (state, action: PayloadAction<PaymentTemplate>) => {
      // Avoid duplicates — upsert by id
      const idx = state.templates.findIndex(t => t.id === action.payload.id);
      if (idx >= 0) {
        state.templates[idx] = action.payload;
      } else {
        state.templates.push(action.payload);
      }
    },
    deleteTemplate: (state, action: PayloadAction<string>) => {
      state.templates = state.templates.filter(t => t.id !== action.payload);
    },
    setCheckoutSession: (state, action: PayloadAction<CheckoutSession>) => {
      state.checkoutSession = action.payload;
    },
    clearCheckoutSession: state => {
      state.checkoutSession = null;
    },
  },
});

export const {
  setRetryPayment,
  clearRetryPayment,
  saveTemplate,
  deleteTemplate,
  setCheckoutSession,
  clearCheckoutSession,
} = paymentSlice.actions;
export { TEMPLATES_STORAGE_KEY };
export default paymentSlice.reducer;
